import React, { useMemo, useState } from "react";
import { RobotOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Checkbox, InputNumber, Popconfirm, Select, Tooltip, Typography } from "antd";

import { PermissionKey } from "@fitness-app/data-models";
import { VideoUploadStatus } from "@fitness-app/data-models/entities/MediaLibrary";
import { StudioClipKind, StudioSegmentRole, type StudioSegment } from "@fitness-app/data-models/entities/StudioClip";

import { usePermission } from "~/hooks/usePermission";
import { AdsChatDrawer } from "./AdsChat";
import { ClipsTable } from "./ClipsTable";
import { useComposeVariants, useStudioSegments } from "../hooks/useStudioAds";
import { useProduceClips, useStudioClips, useStudioRequests, useStudioSources } from "../hooks/useStudioData";
import { isStudioRequestRunning, latestSegmentRequestsBySource } from "../hooks/studio-requests.logic";

const ROLE_COLUMNS: { role: StudioSegmentRole; title: string; hint: string }[] = [
  { role: StudioSegmentRole.Hook, title: "Hooki", hint: "Otwarcie, które zatrzymuje przewijanie" },
  { role: StudioSegmentRole.Value, title: "Value", hint: "Jedna konkretna, wartościowa myśl" },
  { role: StudioSegmentRole.Cta, title: "CTA", hint: "Wezwanie do działania" },
];

const MAX_VARIANTS = 12;

const formatMs = (ms: number) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

/**
 * Reklamy z jednego nagrania (TRE-1975, etap G): agent znajduje hooki, value i CTA,
 * człowiek zaznacza, z czego składać, panel składa warianty hook → value → CTA
 * (2 × 3 × 2 = 12 reklam z jednego nagrania), podgląd jest natychmiastowy, a render
 * dopiero dla wybranych. Human-in-the-loop jak w klipach: nic nie wychodzi bez akceptacji.
 */
export const AdsPanel = (): React.ReactElement => {
  const sourcesQuery = useStudioSources();
  const requestsQuery = useStudioRequests();
  const clipsQuery = useStudioClips();
  const produce = useProduceClips();
  const compose = useComposeVariants();
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);

  const [sourceId, setSourceId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<StudioSegmentRole, string[]>>({ hook: [], value: [], cta: [] });
  const [limit, setLimit] = useState(MAX_VARIANTS);
  const [chatOpen, setChatOpen] = useState(false);

  const readySources = (sourcesQuery.data ?? []).filter(
    (source) => source.data.uploadStatus === VideoUploadStatus.Ready,
  );
  const segmentRequests = latestSegmentRequestsBySource(requestsQuery.data ?? []);
  const request = sourceId ? segmentRequests.get(sourceId) : undefined;
  const isMining = isStudioRequestRunning(request);
  const segmentsQuery = useStudioSegments(request && !isMining ? request.id : null);
  const segments = segmentsQuery.data ?? [];

  const byRole = useMemo(() => {
    const groups: Record<StudioSegmentRole, StudioSegment[]> = { hook: [], value: [], cta: [] };
    for (const segment of segments) groups[segment.role]?.push(segment);
    return groups;
  }, [segments]);

  const variants = (clipsQuery.data ?? []).filter(
    (clip) =>
      clip.kind === StudioClipKind.Variant &&
      clip.sourceMediaId === sourceId &&
      (!request || clip.requestId === request.id),
  );
  const combinations = selected.hook.length * selected.value.length * selected.cta.length;

  const toggle = (role: StudioSegmentRole, id: string, checked: boolean) =>
    setSelected((current) => ({
      ...current,
      [role]: checked ? [...current[role], id] : current[role].filter((entry) => entry !== id),
    }));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px]">
          <Typography.Text type="secondary" className="text-xs">
            Nagranie
          </Typography.Text>
          <Select
            className="w-full"
            showSearch
            optionFilterProp="label"
            placeholder="Wybierz nagranie"
            value={sourceId}
            onChange={(value) => {
              setSourceId(value);
              setSelected({ hook: [], value: [], cta: [] });
            }}
            options={readySources.map((source) => ({ value: source.id, label: source.title }))}
          />
        </div>
        <Popconfirm
          title="Znaleźć hooki, value i CTA?"
          okText="Szukaj"
          cancelText="Anuluj"
          disabled={!sourceId || isMining || !canUseAgents}
          description={
            <div className="max-w-xs">
              Agent przeczyta transkrypcję (istniejąca jest użyta ponownie) i wypisze po kilka najlepszych fragmentów w
              każdej roli, z uzasadnieniem. Bez renderu.
            </div>
          }
          onConfirm={() => sourceId && produce.mutate({ sourceMediaId: sourceId, mode: "segments" })}
        >
          <Button type="primary" disabled={!sourceId || !canUseAgents} loading={isMining || produce.isLoading}>
            {isMining ? "Agent szuka…" : request ? "Szukaj ponownie" : "Znajdź hooki, value i CTA"}
          </Button>
        </Popconfirm>
        <Tooltip title={request && !isMining ? null : "Najpierw znajdź klocki w nagraniu"}>
          <Button
            icon={<RobotOutlined />}
            disabled={!request || isMining || !canUseAgents}
            onClick={() => setChatOpen(true)}
          >
            Porozmawiaj z agentem
          </Button>
        </Tooltip>
        {!canUseAgents && <Typography.Text type="secondary">Brak uprawnienia agents.use</Typography.Text>}
      </div>

      {request?.status === "FAILED" && (
        <Alert type="error" showIcon message={`Ostatnie szukanie nie powiodło się: ${request.data?.error ?? "?"}`} />
      )}

      {request && !isMining && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {ROLE_COLUMNS.map(({ role, title, hint }) => (
              <Card
                key={role}
                size="small"
                title={`${title} (${byRole[role].length})`}
                extra={<span className="text-xs text-gray-400">{hint}</span>}
              >
                {byRole[role].length === 0 && (
                  <Typography.Text type="secondary">Agent nie znalazł nic w tej roli.</Typography.Text>
                )}
                {byRole[role].map((segment) => (
                  <div key={segment.id} className="mb-3 flex items-start gap-2">
                    <Checkbox
                      checked={selected[role].includes(segment.id)}
                      onChange={(event) => toggle(role, segment.id, event.target.checked)}
                    />
                    <div className="min-w-0 flex-1">
                      <Tooltip title={segment.reasoning}>
                        <Typography.Text strong className="block">
                          {segment.quote}
                        </Typography.Text>
                      </Tooltip>
                      <div className="text-xs text-gray-500">
                        {formatMs(segment.startMs)}–{formatMs(segment.endMs)} · ocena {segment.score}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Typography.Text>
              Kombinacje: <strong>{combinations}</strong>
            </Typography.Text>
            <span className="text-xs text-gray-500">max wariantów</span>
            <InputNumber
              min={1}
              max={MAX_VARIANTS}
              value={limit}
              onChange={(value) => setLimit(value ?? MAX_VARIANTS)}
            />
            <Button
              type="primary"
              disabled={combinations === 0 || !canUseAgents}
              loading={compose.isLoading}
              onClick={() =>
                compose.mutate({
                  requestId: request.id,
                  hookIds: selected.hook,
                  valueIds: selected.value,
                  ctaIds: selected.cta,
                  limit,
                })
              }
            >
              Wygeneruj warianty
            </Button>
            <Typography.Text type="secondary" className="text-xs">
              Składanie nic nie kosztuje: podejrzysz każdy wariant od razu, render uruchamiasz osobno.
            </Typography.Text>
          </div>
        </>
      )}

      <AdsChatDrawer
        requestId={request && !isMining ? request.id : null}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onOpen={() => setChatOpen(true)}
      />

      {sourceId && (
        <div>
          <Typography.Title level={5}>Warianty reklam ({variants.length})</Typography.Title>
          <ClipsTable clips={variants} loading={clipsQuery.isLoading} />
        </div>
      )}
    </div>
  );
};
